import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled, { css } from 'styled-components';
import { Row, Button, Separator, Text, Col } from '@cuemath/comps';
import { Header } from 'semantic-ui-react';
import { closeModal } from 'actions/modal';
import COLORS from 'constants/colors';
import {
  INSTRUCTIONS_FOR_EXISTING_PLANS,
  WEBSITE_PLANS_URL,
  FORM_DATA,
  INSTRUCTIONS_FOR_CREATING_PLANS,
  ONLINE_PLANS_POST_URL,
  ONLINE_PLAN_ACTIONS,
  CSV_LINK_FOR_EXISTING_PLANS,
  CSV_LINK_FOR_CREATE_PLANS,
  UPLOAD_FILE,
  UPLOADED_FILE_CONTAINS_ERRORS,
  SUCCESSFULLY_SUBMITTED,
  PLEASE_SELECT_FILE,
  NOTE_FOR_ERROR_COLUMN,
  SOMETHING_WENT_WRONG,
  MULTIPLE_FORM_DATA,
  POST,
  CONTENT_TYPE,
  CSV_CONTENT_TYPE,
  CONTENT_DISPOSITION,
  CSV_LINK,
} from 'constants/pricing-management';
import PropTypes from 'prop-types';
import { CSVLink } from 'react-csv';
import { ButtonWrapper, Field, Head, Message } from 'modules/form';
import axios from 'axios';

const Wrapper = styled.div`
  max-height: 500px;
`;

const AnchorLink = styled.a`
  text-align: left;
  text-decoration: underline;
  color: ${COLORS.BOSTON_BLUE};
  &:hover {
    color: ${COLORS.BOSTON_BLUE};
  }
`;

const FormWrapper = styled.div`
  padding: 20px;
  text-align: center;
`;

const TextWrapper = styled(Text)`
  font-size: 16px;
  ${({ paddingLeft }) =>
    paddingLeft &&
    css`
      padding-left: ${paddingLeft}px;
    `};
`;

const HeadWrapper = styled(Head)`
  border-bottom: 1px solid ${COLORS.DARK_CHARCOAL};
`;

const SpanWrapper = styled.span`
  cursor: pointer;
  color: ${COLORS.BOSTON_BLUE};
`;

const CSVLinkWrapper = styled(CSVLink)`
  display: none;
`;

class OnlinePlansPricingManagementModal extends PureComponent {
  static propTypes = {
    actions: PropTypes.shape({
      closeModal: PropTypes.func.isRequired,
    }).isRequired,
    heading: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      errorText: null,
      webSitePlansData: null,
      file: null,
      message: null,
      errorTextForWebsitePlans: null,
      onlinePlansdata: '',
      noteMsg: null,
      formData: null,
      filename: null,
    };

    this.csvLink = React.createRef();
    this.closeModalTimer = false;
  }

  componentWillUnmount() {
    if (this.closeModalTimer) {
      clearTimeout(this.closeModalTimer);
      this.closeModalTimer = null;
    }
  }

  bakeFormData = isUpdateExistingPlans => {
    const { file } = this.state;

    const { ACTION, FILE, UPDATE, CREATE } = FORM_DATA || {};
    this.setState({
      errorText: null,
    });

    const formData = new FormData();
    formData.append(FILE, file);

    if (isUpdateExistingPlans) {
      formData.append(ACTION, UPDATE);
    } else {
      formData.append(ACTION, CREATE);
    }

    return formData;
  };

  onChangeFunc = event => {
    const { target } = event || {};
    const { files } = target || {};

    const file = Array.from(files) && files.length ? files[0] : null;

    this.setState({
      file,
      errorText: null,
    });
  };

  usageFunc = heading => {
    const { UPDATE_EXISTING_PLANS } = ONLINE_PLAN_ACTIONS || {};

    const useUpdateExistingPlansPoints = [
      'Change the selling price of an existing plan',
      'Change the discount percentage for an existing plan',
      'Hide or show a plan on the website',
    ];

    if (heading === UPDATE_EXISTING_PLANS) {
      return (
        <>
          <Row>
            <TextWrapper display="inline">
              <li>
                Use this facility to{' '}
                <TextWrapper display="inline" weight="semibold">
                  update or edit existing plans
                </TextWrapper>
              </li>
            </TextWrapper>
          </Row>
          <Separator transparent height={25} />
          <TextWrapper>
            <li>You can use this to:</li>
          </TextWrapper>
          {useUpdateExistingPlansPoints.map(value => (
            <TextWrapper key={value} paddingLeft={20}>
              <li>{value}</li>
            </TextWrapper>
          ))}
        </>
      );
    }

    return (
      <Row>
        <TextWrapper display="inline">
          <li>
            Use this facility only to{' '}
            <TextWrapper display="inline" weight="semibold">
              create or deactivate{' '}
            </TextWrapper>
            plans{' '}
            <Text display="inline">
              (If you wish to update or edit existing plans, use the 'Update Existing
              Plans' option)
            </Text>
          </li>
        </TextWrapper>
      </Row>
    );
  };

  linksWrapper = isUpdateExistingPlans => {
    const instructionsLink = isUpdateExistingPlans
      ? INSTRUCTIONS_FOR_EXISTING_PLANS
      : INSTRUCTIONS_FOR_CREATING_PLANS;

    const csvFormatLink = isUpdateExistingPlans
      ? CSV_LINK_FOR_EXISTING_PLANS
      : CSV_LINK_FOR_CREATE_PLANS;

    return (
      <Row>
        <Col xs={3} gutter={0} gap={0}>
          <AnchorLink href={instructionsLink} target="_blank">
            <li>Read Instructions</li>
          </AnchorLink>
        </Col>
        <Col xs={9}>
          <AnchorLink href={csvFormatLink} target="_blank">
            <li>CSV Format to upload</li>
          </AnchorLink>
        </Col>
      </Row>
    );
  };

  onClickDownloadPlans = async () => {
    this.setState({ filename: null });

    await axios
      .get(WEBSITE_PLANS_URL, { params: { website_active: true } })
      .then(response => {
        const { data, headers } = response || {};
        this.fetchFilenameFromHeaders(headers);

        this.setState({ webSitePlansData: data });

        const { current } = this.csvLink || {};
        const { link } = current || {};
        if (current && link) {
          link.click();
        }
      })
      .catch(err => {
        const { response } = err || {};
        const { data } = response || {};

        const { error: errorMsg } = data || {};
        const { message } = errorMsg || {};

        this.setState({ errorTextForWebsitePlans: message });
      });

    this.setState({ webSitePlansData: null });
  };

  downloadOptionForExistingPlan = (isUpdateExistingPlans, webSitePlansData) => {
    const { errorTextForWebsitePlans, filename } = this.state;

    if (!isUpdateExistingPlans) return null;

    return (
      <>
        <Separator height={15} transparent />
        <Row>
          <li>
            <Text display="inline">
              Download plans which are currently visible on website:{' '}
            </Text>

            <SpanWrapper onClick={() => this.onClickDownloadPlans()}>
              Download
            </SpanWrapper>

            {webSitePlansData && (
              <CSVLink
                data={webSitePlansData}
                filename={filename}
                className="hidden"
                ref={this.csvLink}
                target="_blank"
                uFEFF={false}
              />
            )}
          </li>
        </Row>
        <Row>
          {errorTextForWebsitePlans && (
            <Message width="100%" error>
              {errorTextForWebsitePlans}
            </Message>
          )}
        </Row>
      </>
    );
  };

  uploadFilesFunc = isUpdateExistingPlans => {
    const { UPLOAD_UPDATED_PLANS_HERE, UPLOAD_NEW_FILE_HERE } = UPLOAD_FILE || {};

    const headingForFilesUpload = isUpdateExistingPlans
      ? UPLOAD_UPDATED_PLANS_HERE
      : UPLOAD_NEW_FILE_HERE;

    return (
      <Row>
        <Col xs={3} gutter={0} gap={0}>
          {' '}
          <Text display="inline">
            <li>{headingForFilesUpload}</li>
          </Text>
        </Col>
        <Col xs={9}>
          <Field key="fileUpload">
            <input
              name="fileUpload"
              id="inputFile"
              placeholder="Upload File"
              type="file"
              onChange={this.onChangeFunc}
              onClick={event => {
                event.target.value = null;
              }}
              required={true}
              accept=".csv,text/csv"
            />
          </Field>
        </Col>{' '}
      </Row>
    );
  };

  fetchFilenameFromHeaders = headers => {
    let splittedFilename;

    if (headers && headers[CONTENT_DISPOSITION]) {
      splittedFilename = headers[CONTENT_DISPOSITION].split('=');
      if (Array.isArray(splittedFilename) && splittedFilename.length === 2) {
        const filename = splittedFilename[1];
        this.setState({ filename });
      }
    }
  };

  onSubmitFunc = async (event, isUpdateExistingPlans) => {
    const { actions } = this.props;
    const { file } = this.state;

    event.preventDefault();

    const formData = this.bakeFormData(isUpdateExistingPlans);

    if (!file) {
      this.setState({ errorText: PLEASE_SELECT_FILE });
      return;
    }

    this.setState({ onlinePlansdata: '', errorText: null, noteMsg: null });

    await axios
      .request({
        method: POST,
        url: ONLINE_PLANS_POST_URL,
        data: formData,
        headers: {
          encType: MULTIPLE_FORM_DATA,
        },
      })
      .then(res => {
        const { data, headers } = res || {};
        this.setState({ filename: null });
        this.fetchFilenameFromHeaders(headers);

        this.setState({ errorText: null });

        if (data && headers && headers[CONTENT_TYPE].includes(CSV_CONTENT_TYPE)) {
          this.setState({
            errorText: UPLOADED_FILE_CONTAINS_ERRORS,
            onlinePlansdata: data,
            noteMsg: NOTE_FOR_ERROR_COLUMN,
          });

          const ele = document.getElementById(CSV_LINK);
          if (ele) {
            ele.click();
          }
        } else {
          this.setState({ errorText: null, message: SUCCESSFULLY_SUBMITTED });
          this.closeModalTimer = setTimeout(() => {
            actions.closeModal();
          }, 3000);
        }
      })
      .catch(err => {
        const { response } = err || {};
        const { data } = response || {};
        const { error: dataError } = data || {};
        const { message } = dataError || {};

        this.setState({ errorText: message || SOMETHING_WENT_WRONG });
      });
  };

  renderButton = isUpdateExistingPlans => {
    const { onlinePlansdata, filename } = this.state;

    return (
      <Header floated="left">
        <ButtonWrapper flexDirection="row-reverse">
          <CSVLinkWrapper
            data={onlinePlansdata}
            filename={filename}
            uFEFF={false}
            id={CSV_LINK}
          >
            Submit
          </CSVLinkWrapper>

          <Button
            label="Submit"
            onClick={e => this.onSubmitFunc(e, isUpdateExistingPlans)}
          />
        </ButtonWrapper>
      </Header>
    );
  };

  render() {
    const { errorText, message, webSitePlansData, noteMsg } = this.state;
    const { heading } = this.props;

    const { UPDATE_EXISTING_PLANS } = ONLINE_PLAN_ACTIONS || {};

    const isUpdateExistingPlans = heading === UPDATE_EXISTING_PLANS;

    return (
      <Wrapper>
        <HeadWrapper>
          <Text size="xlarge" align="center">
            {heading}
          </Text>
        </HeadWrapper>
        <Separator transparent height={25} />
        <FormWrapper width="100%">
          {this.usageFunc(heading)}
          <Separator transparent height={25} />
          {this.linksWrapper(isUpdateExistingPlans)}
          {this.downloadOptionForExistingPlan(isUpdateExistingPlans, webSitePlansData)}
          <Separator transparent height={25} />
          {this.uploadFilesFunc(isUpdateExistingPlans)}
          {errorText && (
            <Message width="100%" error>
              {errorText}
              {noteMsg && <Row justifyContent="center">{noteMsg}</Row>}
            </Message>
          )}
          {message && <Message width="100%">{message}</Message>}
          <Separator transparent height={25} />
          {this.renderButton(isUpdateExistingPlans)}
        </FormWrapper>
      </Wrapper>
    );
  }
}

export default connect(null, dispatch => ({
  actions: bindActionCreators(
    {
      closeModal,
    },
    dispatch,
  ),
}))(OnlinePlansPricingManagementModal);
